import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { useAccounts } from "../hooks/accounts";
import {
  useBulkCategorize,
  useCategories,
  useCreateTransaction,
  useDeleteTransaction,
  useTransactions,
  useUpdateTransaction,
} from "../hooks/transactions";
import type { TransactionDirection } from "../lib/transactions";

function formatEur(cents: number): string {
  return (cents / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
}

function formatDate(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString("de-DE", { timeZone: "UTC" });
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function TransactionsList() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const accountId = params.get("accountId") ?? undefined;
  const categoryId = params.get("categoryId") ?? undefined;
  const uncategorizedParam = params.get("uncategorized");
  const uncategorized = uncategorizedParam === null ? undefined : uncategorizedParam === "true";
  const direction = (params.get("direction") as TransactionDirection | null) ?? undefined;
  const from = params.get("from") ?? undefined;
  const to = params.get("to") ?? undefined;
  const search = params.get("search") ?? undefined;

  const accounts = useAccounts({ active: "true" });
  const categories = useCategories();
  const tx = useTransactions({ accountId, categoryId, uncategorized, direction, from, to, search });
  const update = useUpdateTransaction();
  const remove = useDeleteTransaction();
  const bulk = useBulkCategorize();
  const create = useCreateTransaction();

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkCategoryId, setBulkCategoryId] = useState("");
  const [showAdd, setShowAdd] = useState(false);

  function setParam(key: string, value: string | null) {
    const next = new URLSearchParams(params);
    if (value === null || value === "") next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  }

  function toggleSelect(id: string) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  }

  function toggleAll() {
    if (!tx.data) return;
    if (selected.size === tx.data.transactions.length) setSelected(new Set());
    else setSelected(new Set(tx.data.transactions.map((t) => t.id)));
  }

  async function applyBulk() {
    if (selected.size === 0) return;
    await bulk.mutateAsync({
      transactionIds: Array.from(selected),
      categoryId: bulkCategoryId || null,
    });
    setSelected(new Set());
    setBulkCategoryId("");
  }

  const list = tx.data?.transactions ?? [];

  const fromBudget = Boolean(from && to);
  const budgetCategoryName = categoryId
    ? (categories.data?.categories ?? []).find((c) => c.id === categoryId)?.fullPath
    : undefined;

  function clearBudgetFilter() {
    const next = new URLSearchParams(params);
    next.delete("from");
    next.delete("to");
    next.delete("categoryId");
    next.delete("uncategorized");
    setParams(next, { replace: true });
  }

  return (
    <section className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div
            className="text-xs font-semibold uppercase tracking-wider mb-1"
            style={{ color: "var(--muted)" }}
          >
            {t("nav.group_cashflow")}
          </div>
          <h1 className="text-2xl font-bold tracking-tight">
            {t("transactions.list.title")}
          </h1>
          <div className="text-sm mt-1" style={{ color: "var(--muted)" }}>
            {list.length}{" "}
            {t("transactions.transactions_plural", { defaultValue: "Buchungen" })}
          </div>
        </div>
        <div className="flex gap-2 items-center">
          <Link
            to="/categories"
            className="text-sm font-medium"
            style={{ color: "var(--accent)" }}
          >
            {t("transactions.list.categories_link")} →
          </Link>
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            className="px-3 py-2 rounded-lg text-white text-sm font-medium"
            style={{ background: "var(--accent)" }}
          >
            + {t("transactions.list.new")}
          </button>
        </div>
      </div>

      {fromBudget && from && (
        <div className="bg-blue-50 border border-blue-200 rounded p-2 text-sm flex items-center justify-between">
          <span>
            {t("transactions.filter.from_budget", {
              category: budgetCategoryName ?? t("budget.uncategorized", { defaultValue: "Ohne Kategorie" }),
              month: from.slice(0, 7),
              defaultValue: "Gefiltert aus Budget {{category}}, {{month}}",
            })}
          </span>
          <button
            type="button"
            onClick={clearBudgetFilter}
            className="text-blue-700 underline text-xs"
          >
            {t("transactions.filter.reset", { defaultValue: "Filter zuruecksetzen" })}
          </button>
        </div>
      )}

      {/* Filter */}
      <div className="bg-white border rounded p-3 flex flex-wrap gap-3 items-end text-sm">
        <label>
          <span className="text-slate-600 block">{t("transactions.list.filter.account")}</span>
          <select
            value={accountId ?? ""}
            onChange={(e) => setParam("accountId", e.target.value || null)}
            className="border rounded px-2 py-1"
          >
            <option value="">{t("transactions.list.filter.all")}</option>
            {(accounts.data?.accounts ?? []).map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span className="text-slate-600 block">{t("transactions.list.filter.category")}</span>
          <select
            value={uncategorized ? "__uncat__" : (categoryId ?? "")}
            onChange={(e) => {
              if (e.target.value === "__uncat__") {
                setParam("categoryId", null);
                setParam("uncategorized", "true");
              } else {
                setParam("uncategorized", null);
                setParam("categoryId", e.target.value || null);
              }
            }}
            className="border rounded px-2 py-1"
          >
            <option value="">{t("transactions.list.filter.all")}</option>
            <option value="__uncat__">{t("transactions.list.filter.uncategorized")}</option>
            {(categories.data?.categories ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.fullPath} ({t(`categories.direction.${c.direction}`)})
              </option>
            ))}
          </select>
        </label>
        <label>
          <span className="text-slate-600 block">{t("transactions.list.filter.from")}</span>
          <input
            type="date"
            value={from ?? ""}
            onChange={(e) => setParam("from", e.target.value || null)}
            className="border rounded px-2 py-1"
          />
        </label>
        <label>
          <span className="text-slate-600 block">{t("transactions.list.filter.to")}</span>
          <input
            type="date"
            value={to ?? ""}
            onChange={(e) => setParam("to", e.target.value || null)}
            className="border rounded px-2 py-1"
          />
        </label>
        <label className="flex-grow min-w-[180px]">
          <span className="text-slate-600 block">{t("transactions.list.filter.search")}</span>
          <input
            type="text"
            value={search ?? ""}
            onChange={(e) => setParam("search", e.target.value || null)}
            placeholder={t("transactions.list.filter.search_placeholder")}
            className="border rounded px-2 py-1 w-full"
          />
        </label>
      </div>

      {/* Bulk-Aktion */}
      {selected.size > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded p-3 flex flex-wrap gap-2 items-center text-sm">
          <span className="font-medium">
            {t("transactions.list.bulk.selected", { count: selected.size })}
          </span>
          <select
            value={bulkCategoryId}
            onChange={(e) => setBulkCategoryId(e.target.value)}
            className="border rounded px-2 py-1"
          >
            <option value="">{t("transactions.list.bulk.uncategorize")}</option>
            {(categories.data?.categories ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.fullPath}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={applyBulk}
            disabled={bulk.isPending}
            className="px-3 py-1 bg-blue-600 text-white rounded disabled:opacity-50"
          >
            {t("transactions.list.bulk.apply")}
          </button>
          <button
            type="button"
            onClick={() => setSelected(new Set())}
            className="px-3 py-1 border rounded"
          >
            {t("transactions.list.bulk.clear")}
          </button>
        </div>
      )}

      {/* Tabelle */}
      {tx.isLoading ? (
        <p>...</p>
      ) : list.length === 0 ? (
        <div className="bg-white border rounded p-6 text-center text-slate-600">
          {t("transactions.list.empty")}
        </div>
      ) : (
        <div className="bg-white border rounded overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="text-left border-b">
                <th className="p-2 w-8">
                  <input
                    type="checkbox"
                    checked={selected.size === list.length && list.length > 0}
                    onChange={toggleAll}
                  />
                </th>
                <th className="p-2">{t("transactions.list.column.date")}</th>
                <th className="p-2">{t("transactions.list.column.description")}</th>
                <th className="p-2">{t("transactions.list.column.account")}</th>
                <th className="p-2">{t("transactions.list.column.category")}</th>
                <th className="p-2 text-right">{t("transactions.list.column.amount")}</th>
                <th className="p-2" />
              </tr>
            </thead>
            <tbody>
              {list.map((t) => (
                <tr key={t.id} className="border-b hover:bg-slate-50">
                  <td className="p-2">
                    <input
                      type="checkbox"
                      checked={selected.has(t.id)}
                      onChange={() => toggleSelect(t.id)}
                    />
                  </td>
                  <td className="p-2 whitespace-nowrap">{formatDate(t.date)}</td>
                  <td className="p-2">
                    {t.description}
                    {t.notes && <p className="text-xs text-slate-500">{t.notes}</p>}
                  </td>
                  <td className="p-2 text-slate-600">{t.account.name}</td>
                  <td className="p-2">
                    <CategoryInlineSelect
                      value={t.categoryId}
                      onChange={(newId) =>
                        update.mutate({ id: t.id, input: { categoryId: newId } })
                      }
                      categories={categories.data?.categories ?? []}
                    />
                  </td>
                  <td
                    className={`p-2 text-right font-mono ${t.amountCents < 0 ? "text-red-700" : "text-emerald-700"}`}
                  >
                    {formatEur(t.amountCents)}
                  </td>
                  <td className="p-2">
                    <button
                      type="button"
                      className="text-red-700 text-xs"
                      onClick={() => {
                        if (window.confirm(t.description)) remove.mutate(t.id);
                      }}
                    >
                      ×
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {tx.data?.hasMore && (
            <p className="p-2 text-xs text-slate-500 text-center">
              {t("transactions.list.has_more", { shown: list.length, total: tx.data.total })}
            </p>
          )}
        </div>
      )}

      {showAdd && (
        <AddTransactionModal
          onCancel={() => setShowAdd(false)}
          onSubmit={async (input) => {
            await create.mutateAsync(input);
            setShowAdd(false);
          }}
        />
      )}
    </section>
  );
}

function CategoryInlineSelect({
  value,
  onChange,
  categories,
}: {
  value: string | null;
  onChange: (newId: string | null) => void;
  categories: { id: string; fullPath: string; color: string | null; direction: string }[];
}) {
  return (
    <select
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value || null)}
      className="border rounded px-1 py-0.5 text-xs"
    >
      <option value="">—</option>
      {categories.map((c) => (
        <option key={c.id} value={c.id}>
          {c.fullPath}
        </option>
      ))}
    </select>
  );
}

function AddTransactionModal({
  onCancel,
  onSubmit,
}: {
  onCancel: () => void;
  onSubmit: (input: {
    accountId: string;
    date: string;
    amountCents: number;
    direction: TransactionDirection;
    description: string;
    notes?: string;
    categoryId?: string | null;
  }) => Promise<void>;
}) {
  const { t } = useTranslation();
  const accounts = useAccounts({ active: "true" });
  const categories = useCategories();
  const [accountId, setAccountId] = useState("");
  const [date, setDate] = useState(todayIso());
  const [amountEur, setAmountEur] = useState("");
  const [direction, setDirection] = useState<TransactionDirection>("EXPENSE");
  const [description, setDescription] = useState("");
  const [notes, setNotes] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [error, setError] = useState<string | undefined>();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(undefined);
    const value = Number(amountEur);
    if (!value) {
      setError("Betrag ?");
      return;
    }
    const signedCents = Math.round(
      direction === "EXPENSE" ? -Math.abs(value * 100) : Math.abs(value * 100),
    );
    try {
      await onSubmit({
        accountId,
        date,
        amountCents: signedCents,
        direction,
        description: description.trim(),
        notes: notes.trim() || undefined,
        categoryId: categoryId || null,
      });
    } catch (err) {
      setError((err as Error).message);
    }
  }

  const dirCategories =
    (categories.data?.categories ?? []).filter((c) => c.direction === direction) ?? [];

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded p-4 w-full max-w-md space-y-3 text-sm"
      >
        <h2 className="font-semibold">{t("transactions.form.new_title")}</h2>
        <label className="block">
          <span className="text-slate-600">{t("transactions.form.account")}</span>
          <select
            required
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
            className="border rounded px-2 py-1 w-full mt-0.5"
          >
            <option value="">…</option>
            {(accounts.data?.accounts ?? []).map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-slate-600">{t("transactions.form.date")}</span>
            <input
              type="date"
              required
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="border rounded px-2 py-1 w-full mt-0.5"
            />
          </label>
          <label className="block">
            <span className="text-slate-600">{t("transactions.form.direction")}</span>
            <select
              value={direction}
              onChange={(e) => {
                setDirection(e.target.value as TransactionDirection);
                setCategoryId("");
              }}
              className="border rounded px-2 py-1 w-full mt-0.5"
            >
              <option value="EXPENSE">{t("categories.direction.EXPENSE")}</option>
              <option value="INCOME">{t("categories.direction.INCOME")}</option>
              <option value="TRANSFER">{t("categories.direction.TRANSFER")}</option>
            </select>
          </label>
        </div>
        <label className="block">
          <span className="text-slate-600">{t("transactions.form.amount")}</span>
          <input
            type="number"
            step="0.01"
            min={0.01}
            required
            value={amountEur}
            onChange={(e) => setAmountEur(e.target.value)}
            className="border rounded px-2 py-1 w-full mt-0.5"
          />
        </label>
        <label className="block">
          <span className="text-slate-600">{t("transactions.form.description")}</span>
          <input
            type="text"
            required
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="border rounded px-2 py-1 w-full mt-0.5"
          />
        </label>
        <label className="block">
          <span className="text-slate-600">{t("transactions.form.category")}</span>
          <select
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
            className="border rounded px-2 py-1 w-full mt-0.5"
          >
            <option value="">—</option>
            {dirCategories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.fullPath}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-slate-600">{t("transactions.form.notes")}</span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="border rounded px-2 py-1 w-full mt-0.5"
          />
        </label>
        {error && <p className="text-red-700 text-xs">{error}</p>}
        <div className="flex gap-2 justify-end">
          <button type="button" onClick={onCancel} className="px-3 py-1 border rounded">
            {t("transactions.form.cancel")}
          </button>
          <button type="submit" className="px-3 py-1 bg-blue-600 text-white rounded">
            {t("transactions.form.save")}
          </button>
        </div>
      </form>
    </div>
  );
}
